import appendQuery from 'append-query'
import { isEmpty, uniqueId } from 'lodash'
import { IMAGING_URL, jsonToFormData } from 'shared/api'
import { GET } from 'shared/util/fetch-methods'
import { SourceEditorEdits } from '../components/source-editor/types'
import { CreationToolSourceFile } from '../types'
import CreationToolOutputType from '../types/output-type'
import getFileFromBase64 from './get-file-from-base64'
import getFramesWithEdits from './get-frames-with-edits'
import getGifFromFrames from './get-gif-from-frames'
import getSourceFrames from './get-source-frames'

const IMAGING_API_BASE_URL = `${IMAGING_URL}/imaging`

const getEditedFile = async (source: File | string, endpoint: string, props: any = {}): Promise<File> => {
    let requestUrl = `${IMAGING_API_BASE_URL}${endpoint}`
    let requestData = GET

    if (typeof source === 'string') {
        requestUrl = appendQuery(requestUrl, {
            ...props,
            media_url: encodeURIComponent(source.replace(/(^\w+:|^)\/\//, '')),
        })
    } else {
        requestData = {
            method: 'POST',
            body: jsonToFormData({
                ...props,
                file: source,
            }),
        }
    }

    const blob = await fetch(requestUrl, requestData).then((response) => response.blob())

    return new File([blob], uniqueId('asset'), {
        type: blob.type,
        lastModified: Date.now(),
    })
}

const getUploadWithEdits = async (
    source: CreationToolSourceFile,
    outputType: CreationToolOutputType,
    edits?: SourceEditorEdits
): Promise<File | string> => {
    let currentFile: File | string = source.source

    // clips
    if (edits && outputType === CreationToolOutputType.Video) {
        const { trim, crop } = edits

        // trim
        if (trim && !isEmpty(trim)) {
            currentFile = await getEditedFile(currentFile, `/trim`, {
                start: trim.start,
                duration: trim.end - trim.start,
                fmt: 'mp4',
            })
        }

        // crop
        if (crop && !isEmpty(crop)) {
            currentFile = await getEditedFile(currentFile, `/crop`, {
                x: crop.x,
                y: crop.y,
                width: crop.width,
                height: crop.height,
                fmt: 'mp4',
            })
        }

        // watermark
        if (edits.frames?.length) {
            const watermarkUrl = edits.frames[0].canvas.toDataURL('image/png', 1)
            const watermarkFile = getFileFromBase64(watermarkUrl, 'image/png')

            currentFile = await getEditedFile(watermarkFile, `/watermark`, {
                media: currentFile,
                fmt: 'mp4',
            })
        }
    } else {
        let frames = source.frames

        if (edits) {
            // get frames if we don't have them
            if (!frames.length) {
                frames = await getSourceFrames({ sourceFile: source })
            }
            frames = getFramesWithEdits(frames, source.duration, edits)
        }

        if (frames.length) {
            currentFile = await getGifFromFrames(frames)
        }
    }

    return currentFile
}

export default getUploadWithEdits
